import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Helmet } from 'react-helmet-async';
import { Outlet, useNavigate } from 'react-router-dom';
import { Slide, ToastContainer } from 'react-toastify';
import { NavbarOrganisation, SidebarOrganisation } from '../../components';
import { ChannelContext, ConfigContext, OrganisationContext, UserContext } from '../../context';
import {
  Channel,
  Organisation,
  useGetConfigQuery,
  useOrganisationQuery,
  User,
} from '../../services';
import {
  getOrganisationDomain,
  LayoutLoader,
  MAIN_CONTENT_ID,
  ScrollToTop,
  SkipLink,
  useConfig,
  useFestiveMode,
  useIsNarrowViewport,
} from '../../shared';
import { useLayoutData } from '../useLayoutData';

import classNames from 'classnames';
import classes from './OrganisationLayout.module.scss';

export const OrganisationLayout: FunctionComponent = () => {
  const navigate = useNavigate();

  const isNarrowViewport = useIsNarrowViewport();

  const { data: layoutData } = useLayoutData({ excludeArchivedChannels: false });

  const { userProfile, userInfo, channelBySubdomain } = { ...layoutData };

  const organisationDomain = useMemo(() => getOrganisationDomain(), []);

  const { data: organisationData } = useOrganisationQuery();

  const { data: config } = useGetConfigQuery({ organisationDomain });

  useConfig(config);

  const { festiveModeComponent } = useFestiveMode({ config });

  const [userProfileData, setUserProfile] = useState<User | undefined>();

  const [channel, setChannel] = useState<Channel | undefined>();

  const [organisation, setOrganisation] = useState<Organisation | undefined>();

  useEffect(() => {
    if (!userProfile) {
      return;
    }

    setUserProfile(userProfile);
  }, [userProfile]);

  useEffect(() => {
    if (!channelBySubdomain) {
      return;
    }

    setChannel(channelBySubdomain);
  }, [channelBySubdomain]);

  useEffect(() => {
    if (!organisationData) {
      return;
    }

    if (!organisationData.role) {
      navigate('/');
    }

    setOrganisation(organisationData);
  }, [navigate, organisationData]);

  if (!userProfileData || !userInfo || !config || !channel || !organisation) {
    return <LayoutLoader />;
  }

  const { title, faviconUrl } = config.elements.meta;

  return (
    <>
      <Helmet>
        <title>{title} - Organisation</title>
        <link rel={'icon'} href={faviconUrl} />
      </Helmet>

      <ScrollToTop />

      <UserContext.Provider value={{ userProfile: userProfileData, setUserProfile, userInfo }}>
        <ConfigContext.Provider value={{ config }}>
          <OrganisationContext.Provider value={{ organisation, setOrganisation }}>
            <ChannelContext.Provider value={{ channel, setChannel }}>
              <div className={classes['layout']}>
                <SkipLink />

                <NavbarOrganisation />

                <div className={classes['layout__page']}>
                  {!isNarrowViewport && <SidebarOrganisation />}

                  <main
                    id={MAIN_CONTENT_ID}
                    className={classNames(classes['layout__content'], {
                      [classes['layout__content--narrow']]: isNarrowViewport,
                    })}
                  >
                    <Outlet />
                  </main>
                </div>
              </div>
            </ChannelContext.Provider>
          </OrganisationContext.Provider>
        </ConfigContext.Provider>
      </UserContext.Provider>

      {createPortal(
        <ToastContainer
          autoClose={2000}
          position={'bottom-center'}
          transition={Slide}
          newestOnTop
          hideProgressBar={true}
          pauseOnHover={false}
          draggable={false}
          style={{ zIndex: 999999 }}
        />,
        document.getElementById('toast-root') as HTMLElement
      )}

      {festiveModeComponent}
    </>
  );
};
